import cn from '../../utils/cn';
import KoobSidebarSection from './KoobSidebarSection';
import KoobSidebarLink from './KoobSidebarLink';
import KoobSidebarUser from './KoobSidebarUser';
import KoobLogo from '../KoobLogo';
import { RouterProvider } from './routerContext';

import useKoobSidebarCollapsed from '../../hooks/useKoobSidebarCollapsed';

export default function KoobSidebar({
  sections = [] as { title?: string; links: any[] }[],
  footerLinks = [],
  user = null,
  impersonating = false,
  onStopImpersonating,
  onLogout,
  // Render-prop slot pinned above the footer (receives the collapsed state),
  // e.g. the TO-app active-folder card.
  bottomSlot,
  // Render-prop slot under the user block (receives the collapsed state),
  // e.g. the organization picker.
  userSlot,
  collapseStorageKey = 'koob-sidebar-collapsed',
  router,
  className,
}) {
  const [collapsed, setCollapsed] = useKoobSidebarCollapsed(collapseStorageKey);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  return (
    <RouterProvider NavLink={router.NavLink} useLocation={router.useLocation}>
      <aside
        className={cn(
          'koob-sidebar fixed top-0 left-0 bottom-0 z-sidebar flex flex-col bg-white border-r border-border transition-all duration-300 overflow-visible',
          collapsed ? 'w-16' : 'w-72',
          className,
        )}
      >
        <style>{`
          .koob-sidebar .koob-sidebar-link.active:not(.koob-sidebar-link--parent),
          .koob-sidebar .koob-sidebar-link.active:not(.koob-sidebar-link--parent):hover {
            background: rgb(var(--accent));
            color: rgb(var(--foreground));
            font-weight: 600;
          }
          .koob-sidebar .koob-sidebar-link.active:not(.koob-sidebar-link--parent)::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            height: 1.25rem;
            width: 3px;
            border-radius: 0 2px 2px 0;
            background: rgb(var(--brand));
          }
        `}</style>

        {/* Header: Logo left + collapse toggle */}
        <div className="shrink-0">
          <div
            className={cn(
              'flex items-center h-20 border-b border-border',
              collapsed ? 'justify-center px-2' : 'justify-between px-6',
            )}
          >
            {!collapsed && (
              <div className="overflow-hidden rounded-lg bg-k-h px-3.5 py-2.5">
                <KoobLogo reverse className="block h-5 w-auto" />
              </div>
            )}
            <button
              onClick={toggleCollapsed}
              className="border-none bg-transparent p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition cursor-pointer shrink-0"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <i
                className={cn(
                  'far fa-fw text-sm',
                  collapsed ? 'fa-angles-right' : 'fa-angles-left',
                )}
              />
            </button>
          </div>

          {/* User info — always shown, with impersonation state */}
          {user && (
            <KoobSidebarUser
              user={user}
              collapsed={collapsed}
              impersonating={impersonating}
              onLogout={onLogout}
              onStopImpersonating={onStopImpersonating}
            />
          )}
          {userSlot && userSlot(collapsed)}
        </div>

        {/* Scrollable navigation */}
        <nav className="flex-1 overflow-y-auto py-5">
          {sections.map((section, index) => (
            <KoobSidebarSection
              key={index}
              title={section.title}
              links={section.links}
              collapsed={collapsed}
            />
          ))}
        </nav>

        {/* Pinned bottom slot (e.g. active-folder card) */}
        {bottomSlot && <div className="shrink-0">{bottomSlot(collapsed)}</div>}

        {/* Footer */}
        {footerLinks.length > 0 && (
          <div className="shrink-0 border-t border-border py-2 px-2 space-y-0.5">
            {footerLinks.map((item, index) => (
              <KoobSidebarLink key={index} link={item} collapsed={collapsed} />
            ))}
          </div>
        )}
      </aside>
    </RouterProvider>
  );
}
